import type { PublicTranslations } from './public-ar';

export const publicEn: PublicTranslations = {
  fulfillment: {
    deliveryOnly: 'This action is available for delivery orders only.',
    pickupOnly: 'This action is available for pickup orders only.',
    busy: 'Order data is changing. Try again, then review the details.',
    amountOutOfRange: 'The amount exceeds the supported range',
    clientUpdateRequired: 'Refresh the page to continue confirming your order.',
    pickupStockNotice: 'Stock is deducted when you confirm the order.',
    pickupNext: 'Collect from the place shown. Contact the store for details.',
    collected: 'Collected',
    legacySubtotal: 'Merchandise subtotal — delivery fee was not recorded',
    quoteFailed: 'Could not calculate the total. Try again.',
    quoteLoading: 'Calculating total',
    loadFailed: 'Could not load fulfillment options. Try again.',
    optionsChanged: 'Options changed. Review the details and total, then confirm again.',
    unavailable: 'Delivery and pickup are currently unavailable',
    chooseMethod: 'Choose delivery or self-pickup',
    pickupPlace: 'Pickup place',
    freePickup: 'Free self-pickup',
    freeDelivery: 'Free delivery',
    deliveryFee: 'Delivery fee',
    pickup: 'Self-pickup',
    delivery: 'Delivery',
    title: 'Fulfillment method',
  },
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    loading: 'Loading\u2026',
    error: 'An error occurred',
    retry: 'Retry',
    search: 'Search',
    call: 'Call',
    actions: {
      close: 'Close',
    },
    phoneCountry: {
      IL: 'Israel',
      PS: 'Palestine',
      JO: 'Jordan',
      SY: 'Syria',
      LB: 'Lebanon',
      EG: 'Egypt',
      TR: 'Turkey',
      US: 'United States',
      international: 'International number',
    },
    languageNames: {
      ar: 'Arabic',
      he: 'Hebrew',
      en: 'English',
    },
    errors: {
      permissionDenied: 'You do not have permission to do that.',
      networkFailed: 'Connection failed. Check your network and try again.',
      storeNotFound: 'This store no longer exists. Refresh the page.',
      unknown: 'Something went wrong. Try again.',
    },
  },
  auth: {
    phoneOtp: {
      phoneLabel: 'Phone number',
      phoneHint: 'Enter the number with country code or choose a country.',
      codeLabel: 'Verification code',
      codeHint: 'Enter the 6-digit code.',
      requestCode: 'Send code',
      requestingCode: 'Sending...',
      verifyCode: 'Verify code',
      verifyingCode: 'Verifying...',
      retryHandoff: 'Try again',
      resendCode: 'Send a new code',
      changePhone: 'Change phone',
      sentMessage: 'We sent a verification code to your phone.',
      smsOnly: 'We will send the code by SMS.',
      existingEmailFallback: "Don't have a verified phone?",
      useEmailFallback: 'Use email and password',
      usePhoneOtp: 'Use phone code',
      errors: {
        phoneRequired: 'Phone number is required',
        phoneInvalid: 'Phone number is not valid',
        codeRequired: 'Verification code is required',
        codeInvalid: 'Enter a 6-digit code',
        invalidCode: 'The code is incorrect or expired',
        customerAccountMissing: 'No customer account exists for this phone. Create one first.',
        rateLimited: 'Too many attempts. Try again in a moment.',
        networkFailed: 'Connection failed. Check your network.',
        cartHandoffFailed: 'You are signed in, but we could not move your cart. Try again.',
        profileEnsureFailed:
          'Your phone was verified, but we could not prepare your customer account. Try again.',
        unknown: 'Something went wrong. Try again.',
      },
    },
  },
  catalog: {
    products: 'Products',
    outOfStock: 'Out of stock',
  },
  checkout: {
    cart: 'Cart',
    total: 'Total',
  },
  customer: {
    phoneCountryLabel: 'Country code',
    phoneCountry: {
      IL: 'Israel',
      PS: 'Palestine',
      JO: 'Jordan',
      SY: 'Syria',
      LB: 'Lebanon',
      EG: 'Egypt',
      TR: 'Turkey',
      US: 'United States',
    },
    signup: {
      title: 'Continue with phone',
      subtitle: 'Use one phone verification code to continue shopping.',
      fullNameLabel: 'Full name',
      fullNameHint: 'How you\u2019d like the seller to address you',
      emailLabel: 'Email',
      emailHint: 'This email will be used to sign in',
      passwordLabel: 'Password',
      passwordHint: 'At least 8 characters',
      passwordConfirmLabel: 'Confirm password',
      phoneLabel: 'Phone number (optional)',
      phoneHint: 'Helps the seller reach you about your order',
      phoneCountryLabel: 'Country code',
      phoneCountry: {
        IL: 'Israel',
        PS: 'Palestine',
        JO: 'Jordan',
        SY: 'Syria',
        LB: 'Lebanon',
        EG: 'Egypt',
        TR: 'Turkey',
        US: 'United States',
      },
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      submit: 'Create account',
      submitting: 'Creating account\u2026',
      haveAccount: 'Already have an account?',
      signInLink: 'Sign in',
      successToast: 'Account created successfully!',
      errors: {
        fullNameRequired: 'Full name is required',
        fullNameTooShort: 'Name is too short',
        fullNameTooLong: 'Name is too long',
        emailRequired: 'Email is required',
        emailInvalid: 'Invalid email address',
        passwordTooShort: 'Password must be at least 8 characters',
        passwordConfirmRequired: 'Password confirmation is required',
        passwordMismatch: 'Passwords do not match',
        phoneInvalid: 'Phone number is not valid for the selected country',
        emailAlreadyExists:
          "This email is already registered. If you're a merchant, sign in with the same account to shop.",
        rateLimited: 'Too many attempts. Please try again in a moment.',
        networkFailed: 'Connection failed. Check your network.',
        accountCreatedSignInFailed: 'Your account was created. Please try to sign in shortly.',
        unknown: 'Something went wrong. Try again.',
      },
    },
    header: {
      signUpLink: 'Continue with phone',
      signInLink: 'Sign in',
      avatarMenuLabel: 'Account menu',
      signOut: 'Sign out',
      myProfile: 'My profile',
      myAddresses: 'My addresses',
      myOrders: 'My orders',
    },
    login: {
      title: 'Continue with phone',
      subtitle: 'New and returning shoppers use the same phone code.',
      emailLabel: 'Email',
      emailHint: 'The address you used when creating your account',
      passwordLabel: 'Password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      submit: 'Sign in',
      submitting: 'Signing in\u2026',
      forgotPasswordLink: 'Forgot password?',
      noAccountYet: 'Don\u2019t have an account?',
      signUpLink: 'Sign up',
      phoneOnlyHint: 'No email or password is needed.',
      errors: {
        emailRequired: 'Email is required',
        emailInvalid: 'Invalid email address',
        passwordRequired: 'Password is required',
        invalidCredentials: 'Invalid credentials, please try again',
        rateLimited: 'Too many attempts. Please try again in a few minutes.',
        networkFailed: 'Connection failed. Check your network.',
        unknown: 'Something went wrong. Try again.',
      },
    },
    passwordReset: {
      request: {
        title: 'Reset your password',
        subtitle: 'We\u2019ll send a reset link to your email.',
        emailLabel: 'Email',
        submit: 'Send reset link',
        submitting: 'Sending\u2026',
        successTitle: 'Check your email',
        successMessage:
          'If an account with this email exists, you\u2019ll receive a message with a reset link within a few minutes.',
        backToSignIn: 'Back to sign in',
        errors: {
          emailRequired: 'Email is required',
          emailInvalid: 'Invalid email address',
          rateLimited: 'Too many attempts. Please try again in a few minutes.',
          networkFailed: 'Connection failed. Check your network.',
          unknown: 'Something went wrong. Try again.',
        },
      },
      confirm: {
        title: 'Choose a new password',
        subtitle: 'Enter a new password to complete the reset.',
        passwordLabel: 'New password',
        passwordHint: 'At least 8 characters',
        passwordConfirmLabel: 'Confirm password',
        showPassword: 'Show password',
        hidePassword: 'Hide password',
        submit: 'Save new password',
        submitting: 'Saving\u2026',
        sessionMissingTitle: 'Reset link expired',
        sessionMissingMessage: 'Please request a new link.',
        sessionMissingCta: 'Request a new link',
        successToast: 'Password changed. Welcome back!',
        errors: {
          passwordTooShort: 'Password must be at least 8 characters',
          passwordMismatch: 'Passwords do not match',
          rateLimited: 'Too many attempts. Please try again in a few minutes.',
          networkFailed: 'Connection failed. Check your network.',
          unknown: 'Something went wrong. Try again.',
        },
      },
    },
    profilePlaceholder: {
      title: 'Profile page coming soon',
      subtitle: 'We\u2019re still building this page. Check back soon.',
      cta: 'Back to the store',
      ariaLabel: 'Profile placeholder page',
    },
    account: {
      pageTitle: 'Account',
      ariaLabel: 'Account page',
      subtitle: 'Manage your profile, saved delivery addresses, and orders.',
      navLabel: 'Account sections',
      items: {
        profile: {
          title: 'Profile',
          description: 'Update your name and preferred language.',
        },
        addresses: {
          title: 'Saved addresses',
          description: 'Add, edit, or choose your default delivery address.',
        },
        orders: {
          title: 'Orders',
          description: 'Review your recent orders and their status.',
        },
      },
    },
    profile: {
      pageTitle: 'Profile',
      ariaLabel: 'Profile page',
      editButton: 'Edit profile',
      addressesButton: 'Manage saved addresses',
      saveButton: 'Save changes',
      saving: 'Saving...',
      cancelButton: 'Cancel',
      nameLabel: 'Name',
      emailLabel: 'Email',
      emailReadOnly: 'Email cannot be changed at this time',
      phoneLabel: 'Phone number',
      phoneOptional: 'Optional',
      phoneReadOnly: 'Phone is tied to sign-in and cannot be changed here.',
      phonePlaceholder: '+972501234567',
      localeLabel: 'Preferred language',
      localeAr: 'Arabic',
      localeHe: 'Hebrew',
      localeEn: 'English',
      successToast: 'Changes saved successfully',
      signOut: 'Sign out',
      signingOut: 'Signing out...',
      deleteAccount: {
        title: 'Delete account',
        warning:
          'This action cannot be undone. Your account will be permanently deleted. Any active orders will remain visible to the merchant with anonymized info.',
        confirmPrompt: 'Type "{{word}}" to confirm',
        confirmWord: 'DELETE',
        confirmButton: 'Delete account permanently',
        deleting: 'Deleting account...',
      },
      errors: {
        nameRequired: 'Name is required',
        nameTooLong: 'Name is too long (max 100 characters)',
        phoneInvalid: 'Invalid phone number',
        networkFailed: 'Connection failed. Check your network.',
        rateLimited: 'Too many attempts. Try again in a few minutes.',
        unknown: 'Something went wrong. Try again.',
        deleteFailed: 'Account deletion failed. Try again.',
      },
    },
    addresses: {
      pageTitle: 'My addresses',
      ariaLabel: 'Addresses page',
      addNewCta: 'Add new address',
      limitReached: "You've reached the limit (20 addresses). Delete an old one to add a new one.",
      defaultBadge: 'Default',
      makeDefault: 'Make default',
      edit: 'Edit',
      delete: 'Delete',
      actionsMenuLabel: 'Address actions',
      empty: {
        title: 'No saved addresses yet',
        body: 'Add one to speed up checkout.',
        cta: 'Add your first address',
      },
      form: {
        addTitle: 'Add new address',
        editTitle: 'Edit address',
        labelLabel: 'Label',
        labelPlaceholder: 'e.g. Home, Work',
        labelOptional: 'Optional',
        recipientNameLabel: 'Recipient name',
        addressLine1Label: 'Address',
        addressLine2Label: 'Address line 2',
        cityLabel: 'City',
        localitySearchPlaceholder: 'Search city or village',
        localitySearchPrompt: 'Start typing to find your city or village.',
        localityNoResults: 'No matching city or village',
        regionLabel: 'Region',
        regionFallbackLabel: 'State / Province',
        postalCodeLabel: 'Postal code',
        countryLabel: 'Country',
        phoneLabel: 'Phone number',
        phoneOptional: 'Optional \u2014 the merchant may use this to coordinate delivery.',
        isDefaultLabel: 'Make this my default address',
        saveNew: 'Save address',
        saveEdit: 'Save changes',
        cancel: 'Cancel',
        saving: 'Saving\u2026',
      },
      districtIL: {
        northern: 'Northern District',
        haifa: 'Haifa District',
        central: 'Central District',
        tel_aviv: 'Tel Aviv District',
        jerusalem: 'Jerusalem District',
        southern: 'Southern District',
      },
      successToast: {
        created: 'Address saved',
        updated: 'Address updated',
        deleted: 'Address deleted',
        defaultSet: 'Default address updated',
      },
      deleteConfirm: {
        title: 'Delete this address?',
        body: 'This address will no longer appear in checkout options. Past orders are not affected.',
        confirm: 'Delete',
        deleting: 'Deleting\u2026',
      },
      errors: {
        labelTooLong: 'Label is too long',
        recipientNameRequired: 'Recipient name is required',
        recipientNameTooLong: 'Recipient name is too long',
        addressLine1Required: 'Address is required',
        addressLine1TooLong: 'Address is too long',
        addressLine2TooLong: 'Address line 2 is too long',
        cityRequired: 'City is required',
        cityTooLong: 'City name is too long',
        regionRequired: 'Region is required',
        regionTooLong: 'Region is too long',
        postalCodeRequired: 'Postal code is required',
        postalCodeInvalidIL: 'Israeli postal code must be 5 or 7 digits',
        postalCodeInvalid: 'Invalid postal code',
        countryRequired: 'Country is required',
        phoneInvalid: 'Invalid phone number',
        networkFailed: 'Connection failed. Check your network.',
        rateLimited: 'Too many attempts. Try again in a few minutes.',
        notFound: 'Address not found.',
        deleteFailed: 'Could not delete the address.',
        setDefaultFailed: 'Could not set the default address.',
        unknown: 'Something went wrong. Try again.',
      },
    },
    // DEPRECATED: F-019 replaced the orders placeholder with the real order
    // history page. These keys are retained for translator reference only.
    // No runtime code references them. Safe to remove in a future cleanup.
    ordersPlaceholder: {
      title: 'Orders coming soon',
      subtitle: 'We\u2019re still building this page. Check back soon.',
      cta: 'Back to the store',
      ariaLabel: 'Orders placeholder page',
    },
    callback: {
      title: 'Verifying\u2026',
      loading: 'One moment while we verify your email.',
    },
    authGate: {
      title: 'Verify your phone to contact the seller',
      subtitle: 'To contact {{storeName}} about {{productName}}, continue with phone verification.',
      signInCta: 'Continue with phone',
      signUpCta: 'Continue with phone',
      closeLabel: 'Close',
      primedBanner: {
        message:
          'You\u2019re signed in \u2014 tap \u201cContact seller\u201d to message {{storeName}}',
        dismissLabel: 'Dismiss',
      },
    },
  },
  cart: {
    errors: {
      insufficientStock:
        'Sorry, someone else just grabbed the last ones \u2014 only {{available}} left now',
      productNotFound: 'Product not found',
      notAuthenticated: 'Please sign in to continue',
      permissionDenied: 'You don\u2019t have permission to make this change',
      networkFailed: 'Network request failed. Please try again.',
      unknown: 'Something went wrong. Please try again.',
      storeUnpublished: 'This store is no longer accepting orders',
      cartItemNotFound: 'That item is no longer in your cart. We\u2019re refreshing it now.',
      invalidQuantity: 'Couldn\u2019t update the quantity. Please try again.',
      variantUnavailable: 'This option is no longer available',
      insufficientVariantStock: 'Not enough stock for this option',
    },
  },
  reviews: {
    verifiedCustomer: 'Verified customer',
    verifiedPurchase: 'Verified purchase',
    merchantResponse: 'Merchant response',
    ratingLabel: '{{rating}} out of 5',
    reviewCount: '{{count}} reviews',
    noReviewsYet: 'No reviews yet',
    errors: {
      notEligible: 'This product is not eligible for review',
      alreadyReviewed: 'This product was already reviewed',
      permissionDenied: 'You do not have permission to submit this review',
      productUnavailable: 'This product cannot be reviewed right now',
      ratingInvalid: 'Choose a rating from 1 to 5',
      textTooLong: 'Review text must be 1000 characters or fewer',
      whitespaceOnly: 'Write clear text or leave the field empty',
    },
  },
  storefront: {
    beauty: {
      shopAll: 'Shop all',
      menu: 'Open store menu',
      closeMenu: 'Close store menu',
      search: 'Search this store',
      searchPlaceholder: 'Search products',
      closeSearch: 'Close search',
      account: 'My account',
      cart: 'Shopping cart',
      productCount: '{{count}} products',
      loadedCount: 'Showing {{loaded}} of {{total}}',
      loadMore: 'Load more',
      sort: 'Sort products',
      clearSearch: 'Clear search',
      noResults: 'No products match your search.',
      selectShade: 'Select shade',
      selectOptions: 'Select options',
      shadeCount: '{{count}} shades',
      shadeCount_one: '1 shade',
      moreDetails: 'More details',
      details: 'Product details',
      shippingReturns: 'Shipping & returns',
      relatedHeading: 'More from this category',
      reviews: 'Reviews',
      ratingSummary: 'Rated {{average}} out of 5 from {{count}} reviews',
      previous: 'Previous',
      next: 'Next',
      community: 'Our community',
      browseProduct: 'View {{name}}',
      shop: 'Shop',
      contact: 'Contact us',
      policies: 'Store policies',
      currency: 'Prices in Israeli shekels',
      newsletterInert: 'Preview only; subscriptions cannot be submitted here.',
      catalogLoadError: 'Products could not be loaded. Try again.',
      factsLoadError: 'Additional product details could not be loaded.',
      chooseOptions: 'Choose all options before adding to cart.',
      backToShop: 'Back to shopping',
      newsletterHeading: 'Stay in touch',
      newsletterBody: 'Subscribe for store updates.',
      galleryIndex: 'Image {{index}} of {{total}}',
      searchSubmit: 'Search',
      footerGroupToggle: 'Toggle {{group}}',
      communityProductUnavailable: 'Product unavailable',
    },
    website: {
      luxury: {
        categories: 'Explore categories',
        discoverCategory: 'Explore collection',
        bestSellers: 'Best Sellers',
        sale: 'Sale',
        newArrivals: 'New Arrivals',
        moreFromCategory: 'More from this category',
        backToCategory: 'Back to collection',
        productDetails: 'Product details',
        merchandisingError: 'Products could not be loaded. Try again.',
      },
      fashion: {
        demoWhiteShirt: 'White button shirt',
        demoBlackBag: 'Black shoulder bag',
        demoCollection: 'The essentials',
        viewCollection: 'View {{category}} collection',
        campaignLoadError: 'We could not load this collection.',
        collectionUnavailableTitle: 'This collection is unavailable right now',
        collectionUnavailableDescription: 'Return to the store to browse available products.',
        retry: 'Try again',
        previousProductImage: 'Previous image of {{name}}',
        nextProductImage: 'Next image of {{name}}',
        productImagePosition: 'Image {{index}} of {{count}} for {{name}}',
        sizeGuideOpen: 'View size guide',
        sizeGuideClose: 'Close size guide',
        newsletterCompany: 'Company',
        sizeGuideTitle: 'Size guide',
        description: 'Description',
        detailsCare: 'Details and care',
        deliveryReturns: 'Delivery and returns',
        generatedArtwork:
          'Original synthetic fashion illustrations generated for Dukanti templates.',
      },
      credits: {
        licenseScope: 'Original photographs and our adaptations use the licenses linked below.',
        title: 'Template photo credits',
        intro:
          'Photography used in Dukanti website templates and their previews. Merchants may replace these images with their own.',
        source: 'Original photograph',
        license: 'License',
        changes:
          'Changes: cropped, resized and compressed to WebP; also used in template preview screenshots.',
      },
      updating: {
        reload: 'Reload this store',
        reloadFailed: 'This store still needs newer code. Try again later.',
        title: 'This store is being updated',
        body: 'Please check back shortly.',
        browse: 'Browse Dukanti',
      },
      skipToContent: 'Skip to content',
      nav: {
        label: 'Store navigation',
        home: 'Home',
        shop: 'Shop',
        about: 'About',
        cart: 'Cart',
        account: 'Account',
        search: 'Search',
        openMenu: 'Open menu',
        closeMenu: 'Close menu',
      },
      hero: {
        previous: 'Previous slide',
        next: 'Next slide',
        slideStatus: 'Slide {{current}} of {{total}}',
      },
      media: {
        pause: 'Pause',
        play: 'Play',
      },
      categories: {
        heading: 'Shop by category',
      },
      products: {
        newArrivals: 'New arrivals',
        bestSellers: 'Best sellers',
        trending: 'Trending now',
        related: 'You may also like',
        viewAll: 'View all',
      },
      about: {
        title: 'About the store',
        delivery: 'Delivery',
        returns: 'Returns',
        contact: 'Contact',
      },
      product: {
        careTitle: 'Care',
        sizeGuideTitle: 'Size guide',
      },
      confirmation: {
        title: 'Order received',
        continue: 'Continue shopping',
      },
      footer: {
        powered: 'Powered by Dukanti',
      },
      canvas: {
        checkoutDisabled:
          'This is your real checkout page. Placing orders is turned off while you edit.',
        sampleProduct: 'Sample product',
        sampleCategory: 'Sample category',
        embeddedBlocked: "This page can't be shown here.",
        orderConfirmationNotice: 'After a customer orders, they see their order confirmation here.',
        seeOrderConfirmation: 'See the order confirmation page',
        notInEditor: "This page isn't part of your storefront editor.",
        backToStore: 'Back to the store home',
      },
    },
    storefrontEngine: {
      fallback: {
        sectionUnavailable: 'This section cannot be shown right now.',
      },
      liveCanvas: {
        empty: {
          title: 'Waiting for builder',
          body: 'This route renders only after the Admin Builder starts a trusted canvas session.',
        },
        loading: 'Waiting for builder',
        ready: 'Canvas ready',
        renderFailed: 'The canvas could not render.',
        invalidPayload: 'The canvas payload was invalid.',
        invalidRenderData: 'The canvas render data was invalid.',
        parityOverlayDisabled: 'Editing layer hidden for comparison.',
        unavailable: 'Live canvas is unavailable.',
        controlsDisabled: 'Canvas controls are temporarily unavailable.',
        selectionLabel: 'Select storefront section',
        sections: {
          hero: 'Hero section',
          header: 'Store header',
          footer: 'Store footer',
          mobileNav: 'Mobile navigation',
          benefits: 'Benefits',
          faq: 'FAQ',
          aboutStore: 'About store',
          testimonials: 'Testimonials',
          newsletterCta: 'Newsletter CTA',
          trustBadge: 'Trust badge',
          promotionalAds: 'Store ads',
          categories: 'Categories',
          liveOffers: 'Live offers',
          products: 'Products',
          trustSummary: 'Trust summary',
        },
        selection: {
          chromeLabel: 'Selected',
          aria: {
            section: 'Selectable section: {{section}}',
            selectedSection: 'Selected canvas section: {{section}}',
          },
        },
        edit: {
          shortLabel: 'Edit',
          editSection: 'Edit {{section}}',
          lockedShortLabel: 'Locked',
          lockedSection: '{{section}} is protected in the builder canvas',
        },
        textFields: {
          editTextField: 'Edit {{field}} in {{section}}',
          labels: {
            heroHeadline: 'Hero headline',
            heroEyebrow: 'Hero eyebrow',
            heroDescription: 'Hero description',
            heroPrimaryActionLabel: 'Hero primary action',
            contentHeading: 'Section heading',
            contentBody: 'Section body',
            contentBenefitTitle: 'Benefit title',
            contentBenefitBody: 'Benefit body',
            contentFaqQuestion: 'FAQ question',
            contentFaqAnswer: 'FAQ answer',
            contentAboutParagraph: 'About paragraph',
            contentTestimonialQuote: 'Testimonial quote',
            contentTestimonialName: 'Testimonial name',
            contentTestimonialContext: 'Testimonial context',
            contentNewsletterButton: 'Newsletter button',
          },
        },
        richContentFields: {
          editRichContentField: 'Edit rich content for {{field}} in {{section}}',
          labels: {
            contentBenefitsBody: 'Benefits body',
            contentBenefitBody: 'Benefit body',
            contentFaqBody: 'FAQ intro',
            contentFaqAnswer: 'FAQ answer',
            contentAboutParagraph: 'About paragraph',
            contentTestimonialsBody: 'Testimonials intro',
            contentTestimonialQuote: 'Testimonial quote',
            contentNewsletterBody: 'Newsletter CTA body',
          },
        },
        mediaFields: {
          replaceMediaField: 'Replace {{field}} in {{section}}',
          labels: {
            heroDesktop: 'Hero desktop image',
            heroMobile: 'Hero mobile image',
            trustBadgeImage: 'Trust badge image',
          },
        },
        add: {
          shortLabel: 'Add',
          addAfterSection: 'Add after {{section}}',
        },
        lifecycle: {
          duplicate: 'Duplicate {{section}}',
          hide: 'Hide {{section}}',
          show: 'Show {{section}}',
          delete: 'Delete {{section}}',
          hiddenPlaceholder: '{{section}} is hidden in the draft.',
        },
        visibility: {
          deviceHiddenPlaceholder: '{{section}} is hidden for this preview size.',
        },
        heroSchedule: {
          always: 'Always active',
          active: 'Live now',
          upcoming: 'Scheduled',
          expired: 'Expired',
        },
        reorder: {
          handle: 'Drag to reorder {{section}}',
          moveUp: 'Move {{section}} before the previous section',
          moveDown: 'Move {{section}} after the next section',
          dropBefore: 'Drop before {{section}}',
          dropAfter: 'Drop after {{section}}',
          movedBefore: '{{section}} moved before {{target}}.',
          movedAfter: '{{section}} moved after {{target}}.',
        },
        inertAction: 'Shopper actions are disabled in the builder canvas.',
        aria: {
          route: 'Storefront builder canvas',
          selectedSection: 'Selected canvas section',
          selectedControls: 'Selected canvas controls for {{section}}',
        },
      },
    },
    preview: {
      banner: 'Preview mode',
      expired: 'This preview link expired.',
      notAvailable: 'Preview is not available.',
      noindexLabel: 'Hidden from search engines',
      unsupportedTitle: 'Preview page not available',
      unsupportedDescription:
        'This destination is not part of the safe preview yet. Continue from the preview home.',
      backToPreview: 'Back to preview home',
    },
    welcome: 'Welcome to Dukanti',
    shopNow: 'Shop now',
    offerCampaigns: {
      homeRailTitle: 'Live campaigns',
      liveBadge: 'Live offer',
      endsIn: 'Ends in {{time}}',
      endedTitle: 'This offer has ended',
      endedDescription: 'Check the store for other offers.',
      emptyTitle: 'No available products in this offer',
      emptyDescription: 'The products may be sold out or no longer be on sale.',
      variantLabel: 'Option: {{variant}}',
      homeCta: 'View offer',
      shareLabel: 'Share offer',
      meta: {
        title: '{{campaignTitle}} — {{storeName}}',
        description: 'A live limited-time offer from {{storeName}}.',
      },
    },
    courierManifest: {
      title: 'Delivery list',
      loading: 'Loading delivery list...',
      retry: 'Retry',
      error: {
        title: 'Could not load this delivery list',
        description:
          'Check your connection and try again. If it keeps failing, ask the store for a new link.',
      },
      batchCode: 'Batch {{displayCode}}',
      courierGreeting: 'For {{name}}',
      expiresAt: 'Expires {{expiresAt}}',
      orders: 'Delivery stops',
      orderCount: '{{count}} orders',
      orderLabel: 'Stop {{displayId}}',
      address: 'Address',
      customerPhone: 'Customer phone',
      googleMaps: 'Google Maps',
      waze: 'Waze',
      packageCount: '{{count}} package',
      packageBatchCount: '{{count}} delivery stops',
      notes: 'Customer notes',
      openMap: 'Open map',
      storeContact: 'Call store: {{phone}}',
      storeContactUnavailable: 'Store contact is unavailable',
      packageTag: 'Package {{tag}}',
      proof: {
        submit: 'Submit delivery proof',
        replace: 'Replace proof',
        submitted: 'Proof submitted',
        choosePhoto: 'Choose photo',
        chooseFromLibrary: 'Choose from library',
        takePhoto: 'Take photo',
        fileSizeKilobytes: '{{count}} KB',
        fileSizeMegabytes: '{{count}} MB',
        errors: {
          invalidType: 'Use a JPG, PNG, or WebP photo.',
          tooLarge: 'Photo must be 5 MB or smaller.',
          notShipped: 'This stop cannot be delivered from here.',
          alreadyConfirmed: 'The store already confirmed this delivery.',
          uploadFailed: 'Could not upload the proof photo. Try again.',
        },
      },
      invalid: {
        title: 'This delivery link is not available',
        body: 'The link may be expired, revoked, or incomplete. Ask the store for a new link.',
        description:
          'The link may be expired, revoked, or incomplete. Ask the store for a new link.',
      },
    },
    courierPortal: {
      title: 'Courier portal',
      invalid: {
        title: 'This courier portal link is not available',
        description: 'The link may be revoked or invalid. Ask the store for a new link.',
      },
      empty: {
        title: 'No packages ready right now',
        description: 'Shipped orders assigned to this courier will appear here.',
      },
      localities: {
        title: 'Localities',
        count: '{{count}} package',
      },
      locality: {
        back: 'Back to localities',
      },
      language: {
        label: 'Language',
        ar: 'Arabic',
        he: 'Hebrew',
        en: 'English',
      },
      device: {
        forgetAction: 'Forget this device',
        title: 'Remove this device?',
        description:
          'Use this if this is not your phone. This device will no longer open the portal unless the store sends the full link again.',
        confirm: 'Remove this device',
      },
      packageCount: '{{count}} package',
      customer: 'Customer',
      proof: {
        required: 'Delivery proof is required before marking this package delivered.',
        submitted: 'Delivery proof has been submitted and is waiting for store confirmation.',
        confirmed: 'Delivery proof has been confirmed by the store.',
      },
    },
    storeChrome: {
      labels: {
        storeHome: 'Store home',
        storeCategories: 'Categories',
        storeSales: 'Sales',
        bestSellers: 'Best sellers',
        trendingNow: 'Trending now',
        newArrivals: 'New arrivals',
        category: 'Category',
        cart: 'Cart',
        account: 'Account',
        orders: 'Orders',
        whatsapp: 'WhatsApp',
        phone: 'Phone',
        instagram: 'Instagram',
        facebook: 'Facebook',
      },
      footer: {
        quickLinks: 'Store links',
        socialLinks: 'Social',
      },
    },
    home: {
      nav: {
        label: 'Home navigation',
        home: 'Home',
        stores: 'Stores',
        sales: 'Sales',
        orders: 'Orders',
        account: 'Account',
      },
      storeNav: {
        home: 'Store',
        sales: 'Sales',
      },
      rootLanding: {
        eyebrow: 'Dukanti',
        title: 'Your local store link is ready when you are',
        subtitle:
          'Use the store link you received, or manage your customer profile and orders from here.',
        profile: 'Profile',
        openStore: 'Open your store',
      },
      hero: {
        badge: 'Shop local',
        title: 'Discover local stores',
        subtitle: 'Browse stores from your village and order directly from their owners.',
        browseStores: 'Browse stores',
        openYourStore: 'Open your store',
        fromProduct: 'From a published product',
        fromStore: 'From a published store',
      },
      products: {
        title: 'Products from local stores',
        empty: 'No published products yet.',
        error: 'Could not load products. Try again.',
        retry: 'Retry',
        viewProductLabel: 'View {{productName}}',
      },
      sales: {
        title: 'Sales and discounts',
        subtitle: 'Discounted products from local stores',
        empty: 'No sales available right now.',
        error: 'Could not load sales. Try again.',
        retry: 'Retry',
      },
      publishedStores: {
        title: 'Local stores',
        empty: 'No published stores yet.',
        error: 'Could not load stores. Try again.',
        retry: 'Retry',
      },
      featuredStores: {
        title: 'Featured stores',
        empty: 'No stores yet. Be the first to open yours!',
        error: 'Could not load stores. Try again.',
        retry: 'Retry',
        viewStore: 'Visit store',
        viewStoreLabel: 'Visit store — {{storeName}}',
        storeLogoAlt: '{{storeName}} logo',
      },
      howItWorks: {
        title: 'How it works',
        step1Title: 'Browse stores',
        step1Description: 'Discover local stores from your village and browse their products.',
        step2Title: 'Contact the seller',
        step2Description: 'Reach out directly to the store owner via WhatsApp or phone.',
        step3Title: 'Order and collect',
        step3Description: 'Complete your order and follow its details from your account.',
      },
      meta: {
        title: 'Dukanti',
        description: 'Customer profile and store links for Dukanti shoppers.',
      },
    },
    sale: {
      onSale: 'Sale',
      discountPercent: '{{percent}}% off',
      originalPrice: 'Original price {{price}}',
    },
    contactSeller: {
      prefilledMessage: "Hi! I'm interested in {{productName}}. Link: {{url}}",
      unavailable: 'Contact not available',
      modal: {
        title: 'How would you like to get in touch?',
        whatsapp: 'Open WhatsApp',
        phone: 'Call',
        close: 'Close',
      },
    },
    storeNotAvailable: {
      title: 'Store not available',
      description: 'This store is currently offline or does not exist.',
      backHome: 'Back to Dukanti',
    },
    storePlaceholder: {
      liveBadge: 'Live',
      comingSoon: 'Full store experience coming soon',
    },
    storeSales: {
      title: 'Sale items',
      subtitle: 'Discounted products from this store only.',
      emptyTitle: 'No sale items right now',
      emptyDescription: 'Check this store again later for new discounts.',
      error: 'Could not load sale items. Try again.',
      meta: {
        title: '{{storeName}} sale items',
        description: 'Browse discounted products from {{storeName}}.',
      },
    },
    storeCollections: {
      bestSellers: {
        title: 'Best Sellers',
      },
      trendingNow: {
        title: 'Trending Now',
      },
      newArrivals: {
        title: 'New Arrivals',
      },
      subtitle: 'Selected products from {{storeName}}',
      emptyTitle: 'No products in this collection yet',
      emptyDescription: 'Check back later as this store receives more activity.',
      meta: {
        title: '{{collectionName}} from {{storeName}}',
      },
    },
    customProductCollections: {
      subtitle: 'Selected products from {{storeName}}',
      emptyTitle: 'No products in this collection right now',
      emptyDescription: 'Check back later as this store updates its collection.',
      meta: {
        title: '{{collectionName}} from {{storeName}}',
        description: 'Browse {{collectionName}} from {{storeName}}.',
      },
    },
    storeCategoriesPage: {
      title: 'All categories',
      subtitle: 'Browse every live category from {{storeName}}',
      emptyTitle: 'No categories yet',
      emptyDescription: 'This store has not published categories with products yet.',
      error: 'Could not load categories. Try again.',
      meta: {
        title: '{{storeName}} categories',
        description: 'Browse all categories from {{storeName}}.',
      },
    },
    storeHeader: {
      homeAriaLabel: 'Back to {{storeName}}',
      storeLogoName: '{{storeName}} store',
      cartAriaLabel: '{{storeName}} cart',
    },
    // F-005 Phase 4a stub strings — final English copy lands in Phase 4c.
    storeHome: {
      header: {
        wordmark: 'dukanti',
        wordmarkAriaLabel: 'Dukanti — home',
      },
      languageSwitcher: 'Language',
      hero: {
        storeLogoAlt: '{{storeName}} logo',
      },
      conversionHero: {
        primaryCta: 'Shop now',
        categoriesCta: 'Browse categories',
        featuredProduct: 'From the store',
        visualFallback: 'A look at {{storeName}}',
        descriptionFallback: 'Selected products from {{storeName}} in one place.',
      },
      contact: {
        title: 'Direct contact with the store',
        description:
          'Choose WhatsApp or a phone call using the contact details published by the merchant.',
        whatsapp: 'WhatsApp',
        phone: 'Call',
        whatsappAriaLabel: 'Contact {{storeName}} on WhatsApp',
        phoneAriaLabel: 'Call {{storeName}}',
      },
      products: {
        title: 'Products',
      },
      sections: {
        hero: 'Storefront',
        contact: 'Direct contact',
        bestSellers: 'Best Sellers',
        trendingNow: 'Trending Now',
        newArrivals: 'New Arrivals',
        specialOffers: 'Special Offers',
        selectedProducts: 'Selected products',
        customCollection: 'Selected collection',
        sales: 'Sales',
        latestProducts: 'Latest products',
        availableNow: 'Available now',
        unavailable: 'Currently unavailable',
        allProducts: 'All products',
        viewAll: 'View all',
        productsUnavailableHint: 'These published products are not available to order right now.',
        emptyManualProducts: 'No selected products can be shown right now.',
        emptyCustomCollection: 'No products from this collection can be shown right now.',
        emptyAutomaticProducts: 'No eligible products can be shown right now.',
        railError: 'Could not load this section.',
        networkError: 'Could not load this section. Check your connection and try again.',
        unknownError: 'Could not load this section right now.',
      },
      newsletter: {
        contactTypeLabel: 'Contact method',
        email: 'Email',
        phone: 'Phone',
        emailLabel: 'Email address',
        phoneLabel: 'Israeli phone number',
        emailPlaceholder: 'name@example.com',
        phonePlaceholder: '050 123 4567',
        consentLabel: 'I agree to receive updates from this store by the contact method I provide.',
        submit: 'Join updates',
        pending: 'Saving...',
        successTitle: 'Thank you',
        successBody: 'Your request was received.',
        invalidEmail: 'Enter a valid email address.',
        invalidPhone: 'Enter a valid Israeli phone number.',
        consentRequired: 'Confirm consent to continue.',
        genericError: 'Could not save right now. Try again later.',
      },
      trust: {
        title: 'Customer trust',
        summary: '{{average}} out of 5 from {{count}} verified reviews',
        badge: '{{average}} · {{count}} verified reviews',
      },
      categories: {
        title: 'Shop by category',
        viewAll: 'View all',
        viewCategory: 'View {{categoryName}}',
        productCount: '{{count}} products',
        loadError: 'Could not load categories.',
        imageAlt: '{{categoryName}} category image',
      },
      ads: {
        slideAriaLabel: 'Ad {{index}} of {{total}}',
        openAd: 'Open ad',
        destinationCue: 'View details',
        imageAlt: 'Ad from {{storeName}}',
        previous: 'Previous ad',
        next: 'Next ad',
        goToSlide: 'Show ad {{index}}',
      },
      productImageAlt: '{{productName}} image',
      viewProduct: 'View product',
      loadMore: 'Load more',
      loadingMore: 'Loading\u2026',
      productCard: {
        availability: {
          outOfStock: 'Out of stock',
          onlyNLeft: 'Only {{count}} left',
        },
      },
      empty: {
        title: 'No products yet',
        description: "This store hasn't added any products yet — check back later.",
      },
      errors: {
        loadMoreFailed: "Couldn't load more. Try again.",
        networkFailed: 'Connection failed. Check your network.',
        unknown: 'Something went wrong. Try again.',
        loadFirstFailed: "Couldn't load products right now.",
        loadFirstFailedHint: 'Check your connection and try again.',
      },
      liveRegion: {
        loaded: 'Showing {{count}} loaded products',
        loadedFiltered: 'Showing {{visibleCount}} of {{loadedCount}} loaded products',
      },
      loadedFilter: {
        label: 'Filter loaded products',
        placeholder: 'Product name in loaded products',
        summary: 'Showing {{visibleCount}} of {{loadedCount}} loaded products',
        clear: 'Clear filter',
        hideUnavailable: 'Hide out-of-stock loaded products',
        emptyTitle: 'No loaded products match that name',
        emptyDescription: 'Clear the filter or load another page into the shown products.',
      },
      loading: {
        srLabel: 'Loading store',
        ariaLabel: 'Loading store',
      },
      seo: {
        title: '{{storeName}}',
        fallbackDescription: 'Shop {{storeName}}',
      },
    },
    productCard: {
      fromPrice: 'From {{price}}',
      saleFromPrice: 'Sale from {{price}}',
    },
    categoryCatalog: {
      backToStore: 'Back to store',
      productCount: '{{count}} products',
      search: {
        label: 'Search within {{categoryName}}',
        placeholder: 'Search products in this category',
        clear: 'Clear search',
      },
      sort: {
        label: 'Sort products',
        default: 'Default',
        priceAsc: 'Price: low to high',
        priceDesc: 'Price: high to low',
        newest: 'Newest',
        nameAsc: 'Name A-Z',
      },
      loading: 'Loading category products...',
      empty: {
        title: 'No matching products',
        description: "Try another search or clear it to see this category's products.",
      },
      emptySearch: {
        title: 'No products match {{search}}',
        description: "Try another search or clear it to see this category's products.",
        clear: 'Clear search',
      },
      errors: {
        loadFailed: 'Could not load category products.',
        loadMoreFailed: 'Could not load more category products.',
        networkFailed: 'Could not connect. Try again.',
        unknown: 'Something went wrong.',
      },
      liveRegion: {
        loaded: 'Showing {{visibleCount}} of {{totalCount}} products',
      },
      meta: {
        title: '{{categoryName}} — {{storeName}}',
        description: 'Shop {{categoryName}} products from {{storeName}}.',
      },
    },
    productPlaceholder: {
      title: 'Product page coming soon',
      description:
        'We are building product pages next. Head back to the store to browse what is available.',
      backToStore: 'Back to store',
    },
    productDetail: {
      backToStore: 'Back to {{storeName}}',
      categoryLabel: 'Category',
      descriptionLabel: 'Description',
      contactSeller: 'Contact seller',
      outOfStock: 'Out of stock',
      availability: {
        inStock: 'In stock',
        lowStock: 'Only {{count}} left',
        outOfStock: 'Out of stock',
      },
      variants: {
        chooseOption: 'Choose {{option}}',
        available: 'Available',
        disabled: 'Not available for sale',
        unavailable: 'Unavailable',
        outOfStock: 'Out of stock',
        lowStock: 'Only {{count}} left',
        selectRequired: 'Choose an option before adding to cart',
      },
      quantity: {
        label: 'Quantity',
        decreaseAriaLabel: 'Decrease quantity',
        increaseAriaLabel: 'Increase quantity',
        helperMustBeBetween: 'Enter a whole number between 1 and {{max}}',
        helperExceedsAvailable: 'Only {{available}} available',
        helperOnlyNLeft: 'Only {{count}} left',
        outOfStockReplacement: 'Out of stock',
        maxInCartReplacement: 'Maximum already in your cart',
      },
      gallery: {
        previousImage: 'Previous image',
        nextImage: 'Next image',
        goToImage: 'Go to image {{index}}',
        paginationLabel: 'Image pagination',
        thumbnailLabel: 'Image {{index}} thumbnail',
        noImages: 'No images for this product',
        zoomImage: 'Zoom image {{index}}',
        zoomOverlayLabel: 'Full-size image viewer',
        zoomClose: 'Close',
        zoomCounter: '{{current}} / {{total}}',
        zoomResetZoom: 'Reset zoom',
      },
      seo: {
        title: '{{productName}} — {{storeName}}',
        fallbackDescription: 'Shop {{productName}} from {{storeName}}',
      },
      loading: {
        srLabel: 'Loading product',
      },
      share: {
        button: 'Share',
        copyButton: 'Copy link',
        shareAriaLabel: 'Share {{productName}}',
        copyAriaLabel: 'Copy link to {{productName}}',
        nativeText: '{{productName}} from {{storeName}}',
        toast: {
          sharedTitle: 'Product shared',
          copiedTitle: 'Link copied',
          failedTitle: 'Couldn\u2019t share',
          failedBody: 'We couldn\u2019t share or copy this product link. Try again.',
        },
      },
      reviews: {
        title: 'Customer reviews',
        summary: '{{average}} · {{count}} reviews',
        retry: 'Reload reviews',
        yourReviewTitle: 'Your review',
        editYourReview: 'Edit review',
        editTitle: 'Edit your review',
        saveEdit: 'Save review',
        editSuccess: 'Your review was updated',
        hiddenOwnReview:
          'This review is hidden from shoppers for now. Your changes stay attached to the same verified purchase.',
      },
    },
    cart: {
      addToCart: 'Add to cart',
      adding: 'Adding\u2026',
      outOfStockButton: 'Out of stock',
      maxInCartButton: 'Maximum in cart',
      persistenceError:
        'We could not save this item in your cart on this browser. Check storage or private browsing settings, then try again.',
      successToast: {
        title: 'Added to cart',
        body: '{{productName}} \u00d7 {{quantity}}',
        viewCart: 'View cart',
      },
      errorToast: {
        title: 'Couldn\u2019t add to cart',
      },
      line: {
        variantSummary: '{{summary}}',
      },
      primedBanner: {
        title: 'Picking up where you left off \u2014 add to {{storeName}}',
        dismiss: 'Dismiss',
      },
      postAddPanel: {
        title: '{{productName}} was added to your cart',
        body: 'Your cart now has {{quantity}}.',
        viewCart: 'View cart',
        continueShopping: 'Continue shopping',
        dismiss: 'Dismiss',
      },
      // F-015: cart header icon + badge + dropdown.
      // English CLDR plural rules use only _one (count=1) and _other
      // (everything else). We seed _zero, _two, _few, _many with _other
      // to satisfy the i18n parity test (set-equality with ar.ts / he.ts).
      header: {
        // iconLabel (6 forms)
        iconLabel_zero: 'Cart \u2014 empty',
        iconLabel_one: 'Cart \u2014 1 item',
        iconLabel_two: 'Cart \u2014 {{count}} items', // seeded = _other
        iconLabel_few: 'Cart \u2014 {{count}} items', // seeded = _other
        iconLabel_many: 'Cart \u2014 {{count}} items', // seeded = _other
        iconLabel_other: 'Cart \u2014 {{count}} items',

        // static strings
        dropdownTitle: 'Your cart',
        dropdownAriaLabel: 'Cart preview',
        subtotal: 'Subtotal',
        viewCart: 'View cart',
        emptyTitle: 'Your cart is empty',
        browseProducts: 'Browse products',
        // Security W4: pure translated string — no interpolation allowed.
        loadError: 'Couldn\u2019t load your cart',

        // itemCountSuffix (6 forms)
        itemCountSuffix_zero: 'No items',
        itemCountSuffix_one: '1 item',
        itemCountSuffix_two: '{{count}} items',
        itemCountSuffix_few: '{{count}} items',
        itemCountSuffix_many: '{{count}} items',
        itemCountSuffix_other: '{{count}} items',

        // moreItems (6 forms)
        moreItems_zero: '+0 more',
        moreItems_one: '+1 more',
        moreItems_two: '+{{count}} more',
        moreItems_few: '+{{count}} more',
        moreItems_many: '+{{count}} more',
        moreItems_other: '+{{count}} more',

        // itemsUnavailable (6 forms)
        itemsUnavailable_zero: 'No items unavailable',
        itemsUnavailable_one: '1 item no longer available',
        itemsUnavailable_two: '{{count}} items no longer available',
        itemsUnavailable_few: '{{count}} items no longer available',
        itemsUnavailable_many: '{{count}} items no longer available',
        itemsUnavailable_other: '{{count}} items no longer available',
      },
      // F-016: full cart page strings.
      page: {
        title: 'Your cart',
        subtitle: 'Review your items before checkout',
        breadcrumbBack: 'Back to store',
        empty: {
          title: 'Your cart is empty',
          body: 'You haven\u2019t added anything to your cart yet.',
          browseProducts: 'Browse products',
        },
        line: {
          removeAriaLabel: 'Remove {{productName}} from cart',
          unitPrice: '{{price}} each',
          lineSubtotal: 'Line subtotal',
          priceUpdated: 'Price updated',
          priceWas: 'Was: {{oldPrice}}',
          priceNow: 'Now: {{newPrice}}',
          unavailable: 'Unavailable',
          unknownProduct: 'Product no longer available',
          imageAlt: '{{productName}} image',
        },
        unavailableBanner: {
          title: 'Some items are no longer available',
          body: '{{count}} items in your cart can no longer be purchased. Remove them to continue to order confirmation.',
        },
        handoffRecovery: {
          retrying: 'Moving your cart after sign-in...',
          partial:
            'We could not move every cart item. Review your cart, then try confirming the order again.',
          savedIntentTitle: 'Some saved items need review',
          savedIntentBody:
            'These items are still saved on this browser and have not moved into your account cart. Remove anything you no longer want, then try confirming the order again.',
          retrySavedItems: 'Try moving saved items again',
        },
        summary: {
          title: 'Order summary',
          subtotal: 'Subtotal',
          itemCount_zero: 'No items',
          itemCount_one: '1 item',
          itemCount_two: '{{count}} items', // seeded = _other (English CLDR has only _one + _other)
          itemCount_few: '{{count}} items', // seeded = _other
          itemCount_many: '{{count}} items', // seeded = _other
          itemCount_other: '{{count}} items',
          stockCheckNote:
            'Items stay in your cart without holding stock. Availability is confirmed when you submit the order.',
          proceedToCheckout: 'Continue to confirm order',
          checkoutDisabledHint: 'Remove unavailable items to continue',
          checkoutDisabledHandoffHint: 'Review or remove saved cart items before continuing',
          signinRequiredDialog: {
            title: 'Verify your phone to confirm the order',
            body: 'We will send one code, then bring you back to confirm the order. Your cart stays saved here.',
            continue: 'Continue with phone',
            cancel: 'Stay in cart',
          },
        },
        removeDialog: {
          title: 'Remove {{productName}}?',
          body: '{{productName}} will be removed from your cart.',
          confirm: 'Remove',
          cancel: 'Cancel',
        },
        errors: {
          refetchFailed: 'Couldn\u2019t load your cart. Please try again.',
          updateFailed: 'Couldn\u2019t update your cart',
          removeFailed: 'Couldn\u2019t remove the item from your cart',
        },
        toast: {
          removed: 'Removed {{productName}} from your cart',
        },
        skeleton: {
          loadingAriaLabel: 'Loading your cart',
        },
        retryButton: 'Try again',
      },
    },
    // F-017: real checkout flow.
    checkout: {
      review: {
        cartChanged: 'Your cart items changed. Review the updated cart, then confirm again.',
        cartRefreshFailed: 'Could not refresh your cart. Try again before confirming the order.',
        verificationFailed: 'Could not verify your cart items. Try again before confirming.',
      },
      page: {
        title: 'Checkout',
        backToCart: 'Back to cart',
        shippingSection: 'Shipping address',
        changeAddress: 'Change address',
        useDifferentAddress: 'Use a different address',
        manageAddresses: 'Manage saved addresses',
        saveForNextTime: 'Save this address for next time',
        localityPlaceholder: 'Choose city or village',
        localityLoading: 'Loading cities...',
        localityError: 'Could not load cities. Try again.',
        notesSection: 'Notes for seller',
        submitCta: 'Submit order',
        deliveryPriceNoticeTitle: 'Delivery price will be confirmed separately',
        deliveryPriceNotice:
          'Delivery price is not included yet. The store will contact you to confirm the delivery cost before sending your order.',
      },
      deliveryMethod: {
        title: 'Delivery method',
        location: 'Location',
        address: 'Address',
      },
      deliveryInfo: {
        title: 'Delivery information',
        description: 'Tell the store where to deliver. Precise location is optional.',
        localityLabel: 'City or village',
        postalCodeLabel: 'Postal code',
        recipientLabel: 'Recipient name',
        backupPhoneLabel: 'Backup phone',
        errors: {
          localityRequired: 'Choose a city or village.',
          postalCodeRequired: 'Enter a postal code.',
          postalCodeInvalid: 'Enter a valid Israeli postal code.',
          recipientRequired: 'Enter the recipient name.',
          recipientTooLong: 'Recipient name is too long.',
          backupPhoneInvalid: 'Enter a valid backup phone number.',
        },
      },
      deliveryLocation: {
        title: 'Precise delivery location',
        description: 'Share your current location so the merchant can deliver to the exact point.',
        localityLabel: 'City or village',
        localityRequired: 'Choose a city or village before sharing your location.',
        homeWarning:
          'Use this option only when you are at home or at the location where you usually receive deliveries.',
        cta: 'Share my precise location',
        update: 'Update precise location',
        locating: 'Finding location...',
        shared: 'Precise location added for delivery',
        clear: 'Remove location',
        denied: 'Location permission was denied. You can still complete checkout without GPS.',
        deniedWithExisting:
          'Location permission was denied. Your previously added precise location is still attached to this order.',
        unavailable:
          'Precise location is unavailable on this device. You can still complete checkout without GPS.',
        unavailableWithExisting:
          'Precise location is unavailable on this device. Your previously added location is still attached to this order.',
      },
      locationModal: {
        title: 'Share precise location?',
        body: 'Your browser will ask for permission. We will add these coordinates only to this order.',
        confirmCta: 'Share location',
        cancelCta: 'Cancel',
      },
      modal: {
        title: 'Confirm order',
        body: 'You are about to submit an order to {{storeName}}.',
        itemCount_one: '1 item',
        itemCount_other: '{{count}} items',
        subtotal: 'Subtotal',
        confirmCta: 'Confirm',
        cancelCta: 'Cancel',
      },
    },
  },
  // F-017: order domain namespace.
  order: {
    submit: 'Submit order',
    confirm: 'Confirm order',
    cancel: 'Cancel order',
    placed: 'Order placed',
    tracking: 'Track order',
    shippingAddress: 'Shipping address',
    deliveryInformation: 'Delivery information',
    preciseLocation: 'Precise delivery location',
    orderSummary: 'Order summary',
    stockCheckNotice:
      'Stock is reserved when you submit the order. The seller will contact you to arrange delivery.',
    notes: {
      placeholder: 'Add notes for the seller (optional)',
      maxLengthHint: '{{current}}/{{max}}',
    },
    status: {
      ordered: 'Ordered',
      shipped: 'Shipped',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      rejected: 'Rejected',
    },
    // F-018: order confirmation page keys
    headline: {
      placed: 'Order placed!',
      ordered: 'Order received!',
      shipped: 'Order shipped!',
      delivered: 'Order delivered!',
      cancelled: 'Order cancelled',
      rejected: 'Order rejected',
    },
    whatHappensNext: 'What happens next?',
    whatHappensNextBody:
      'The merchant will review your order and contact you via WhatsApp or phone to confirm delivery and arrange payment. You can track the status of this order here any time.',
    contactMerchant: 'Contact merchant',
    contactMerchantWhatsappMessage:
      "Hello {{storeName}}, I'd like to ask about my order {{orderId}}",
    contactMerchantWhatsappAriaLabel: 'Message {{storeName}} on WhatsApp about this order',
    contactMerchantPhoneAriaLabel: 'Call {{storeName}} about this order',
    id: 'Order #',
    placedDate: 'Placed on',
    cancelButton: 'Cancel order',
    cancelQuestion: 'Changed your mind?',
    cancelConfirmTitle: 'Cancel this order?',
    cancelConfirmBody: 'This will cancel the order and restore the items to the store inventory.',
    cancelConfirmCta: 'Yes, cancel order',
    cancelSuccess: 'Your order has been cancelled',
    timeline: {
      title: 'Order status',
      completedOn: 'Completed on {{- date}}',
    },
    viewAllOrders: 'View all my orders',
    backToStore: 'Back to store',
    customerNotes: 'Your notes to the merchant',
    loadingDetails: 'Loading order details',
    reviewPrompt: {
      title: 'Reviews for this order',
      submit: 'Submit review',
      alreadyReviewed: 'Already reviewed',
      ratingRequired: 'Choose a rating from 1 to 5',
      textTooLong: 'Review text must be 1000 characters or fewer',
      whitespaceOnly: 'Write clear text or leave the field empty',
      success: 'Your review was submitted',
      openDialog: 'Write a review',
      ratingOnlyHint: 'You can submit a star rating only or add a short note.',
      reviewedHint: 'Your submitted reviews stay attached to the delivered products.',
      yourReview: 'Your review',
      editReview: 'Edit review',
      hiddenReview: 'Hidden from shoppers for now. Your edit keeps the verified purchase link.',
      merchantResponse: 'Merchant response',
      textLabel: 'Review text',
      textPlaceholder: 'Write what you thought about the product',
      dialogTitle: 'Review {{productName}}',
      close: 'Close',
    },
    errors: {
      productUnavailable: 'Some products are no longer available: {{names}}',
      variantUnavailable: 'One option is no longer available. Review your cart.',
      cartEmpty: 'Your cart is empty. Add products before checking out.',
      storeUnavailable: 'This store is not accepting orders at this time.',
      networkFailed: 'Connection failed. Check your network and try again.',
      unknown: 'Something went wrong. Please try again.',
      // F-018: cancel-specific error keys
      cancelFailed: 'Could not cancel order. Try again.',
      cancelAlreadyProcessed: 'This order can no longer be cancelled.',
      orderNotFound: 'Order not found.',
    },
    history: {
      title: 'My Orders',
      totalOrders: '{{count}} orders',
      activeOrders: '{{count}} active',
      filterLabel: 'Filter by status',
      filterAll: 'All',
      itemCount: '{{count}} items',
      viewOrder: 'View',
      loadMore: 'Load more',
      loadingMore: 'Loading more...',
      loadingOrders: 'Loading orders',
      backToAccount: 'Account',
      emptyAllCta: 'Browse stores',
      emptyAllSubtitle: 'Browse stores and start shopping',
      emptyFilteredHint: 'Try a different filter or check back later',
      empty: {
        all: "You haven't placed any orders yet",
        ordered: 'No orders awaiting shipment',
        shipped: 'No shipped orders',
        delivered: 'No delivered orders',
        cancelled: 'No cancelled orders',
        rejected: 'No rejected orders',
      },
    },
    // F-020: status-change toast key
    statusChangedToast: 'Your order is now {{status}}',
  },
  errors: {
    notFound: 'Page not found',
    networkFailed: 'Network request failed',
    title: 'Something went wrong',
    description: 'We are sorry for the inconvenience. You can try again.',
    retry: 'Try again',
  },
};
